// ---------------
// FICHIER : src/pages/FAQ.js
// Page FAQ pour l'API Mobile Money WonyaPay
// ---------------
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_PORTAL_URL } from '../constants/links';

const FAQ = () => {
    const [activeQuestion, setActiveQuestion] = useState(null);
    const [showScrollButton, setShowScrollButton] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 300) {
                setShowScrollButton(true);
            } else {
                setShowScrollButton(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleQuestion = (index) => {
        setActiveQuestion(activeQuestion === index ? null : index);
    };

    const faqData = [
        {
            question: "🚀 Comment commencer avec l'API WonyaPay ?",
            answer: "Commencez par télécharger l'application WonyaPay sur votre téléphone, créez une caisse pour recevoir les paiements, puis contactez notre équipe technique pour obtenir vos clés d'accès à l'API. Consultez notre documentation complète pour les détails techniques."
        },
        {
            question: "💰 Quels sont les frais d'intégration ?",
            answer: "L'intégration de base est gratuite. Nous appliquons uniquement des frais de transaction compétitifs sur chaque paiement réussi. Contactez notre équipe commerciale pour obtenir une grille tarifaire détaillée adaptée à votre volume."
        },
        {
            question: "🔒 L'API est-elle sécurisée ?",
            answer: "Absolument ! Notre API utilise le chiffrement HTTPS/TLS, l'authentification par clés API, et respecte les standards de sécurité bancaire. Toutes les transactions sont sécurisées et conformes aux réglementations de la Banque Centrale du Congo."
        },
        {
            question: "📱 Quels opérateurs Mobile Money sont supportés ?",
            answer: "Notre API supporte tous les principaux opérateurs de RDC : M-Pesa (Vodacom), Orange Money et Airtel Money. Nous ajoutons régulièrement de nouveaux partenaires."
        },
        {
            question: "⏱️ Combien de temps prend une transaction ?",
            answer: "Les transactions sont généralement traitées en temps réel (quelques secondes). Dans de rares cas, cela peut prendre jusqu'à 2-3 minutes selon l'opérateur mobile. Vous recevrez une notification callback dès que le statut est confirmé."
        },
        {
            question: "🔄 Comment fonctionne le système de callback ?",
            answer: "Après chaque transaction, notre API envoie automatiquement une requête POST à votre URL de callback avec le statut de la transaction (Succès, Échec, En attente, Annulée). Cela vous permet de mettre à jour votre système en temps réel."
        },
        {
            question: "💸 Que se passe-t-il si une transaction échoue ?",
            answer: "En cas d'échec, aucun montant n'est débité du compte client. Vous recevrez un callback avec le statut 'Echec' et la raison (solde insuffisant, compte bloqué, etc.). Aucuns frais ne vous sont facturés pour les transactions échouées."
        },
        {
            question: "🛠️ Puis-je tester l'API avant la mise en production ?",
            answer: "Oui ! Nous fournissons un environnement de test (sandbox) avec des comptes fictifs pour tester toutes les fonctionnalités. Contactez notre équipe technique pour obtenir vos clés de test."
        },
        {
            question: "📊 Y a-t-il des limites de transaction ?",
            answer: "Les limites dépendent de l'opérateur mobile et du type de compte client. Généralement : minimum 100 CDF, maximum 2,000,000 CDF par transaction. Pour l'USD : minimum 1 USD, maximum 5,000 USD par transaction."
        },
        {
            question: "🌍 L'API fonctionne-t-elle à l'international ?",
            answer: "Actuellement, notre API est optimisée pour la République Démocratique du Congo. Nous travaillons sur l'extension vers d'autres pays d'Afrique. Restez connectés pour les mises à jour !"
        },
        {
            question: "📞 Comment obtenir de l'aide technique ?",
            answer: "Notre équipe de support technique est disponible par email (support@wonyasoft.com), téléphone (+243 810 022 154), ou chat direct. Nous proposons aussi une documentation détaillée et des exemples de code."
        },
        {
            question: "🔧 Puis-je personnaliser les messages de transaction ?",
            answer: "Oui, vous pouvez personnaliser le champ 'Motif' pour décrire la transaction selon vos besoins (ex: 'Paiement commande #12345', 'Abonnement mensuel', etc.). Le client verra cette description sur son téléphone."
        }
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="page-hero faq-hero">
                <div className="page-hero-content">
                    <h1>❓ Questions Fréquentes</h1>
                    <p>Tout ce que vous devez savoir sur l'API Mobile Money WonyaPay</p>
                    <div className="faq-stats">
                        <div className="stat">
                            <span className="stat-number">{faqData.length}</span>
                            <span className="stat-label">Questions</span>
                        </div>
                        <div className="stat">
                            <span className="stat-number">24/7</span>
                            <span className="stat-label">Support</span>
                        </div>
                        <div className="stat">
                            <span className="stat-number">99.9%</span>
                            <span className="stat-label">Disponibilité</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Search Section */}
            <section className="faq-search">
                <div className="search-container">
                    <h2>🔍 Rechercher dans la FAQ</h2>
                    <div className="search-box">
                        <input 
                            type="text" 
                            placeholder="Tapez votre question ou mot-clé..." 
                            className="search-input"
                        />
                        <button className="search-btn">Rechercher</button>
                    </div>
                </div>
            </section>

            {/* FAQ Content */}
            <section className="faq-content">
                <div className="faq-container">
                    {faqData.map((item, index) => (
                        <div key={index} className="faq-item">
                            <button
                                type="button"
                                className="faq-question"
                                onClick={() => toggleQuestion(index)}
                                aria-expanded={activeQuestion === index}
                                aria-controls={`faq-answer-${index}`}
                                id={`faq-question-${index}`}
                            >
                                <h3>{item.question}</h3>
                                <span className={`faq-toggle ${activeQuestion === index ? 'active' : ''}`}>
                                    {activeQuestion === index ? '−' : '+'}
                                </span>
                            </button>
                            {activeQuestion === index && (
                                <div
                                    className="faq-answer"
                                    id={`faq-answer-${index}`}
                                    role="region"
                                    aria-labelledby={`faq-question-${index}`}
                                >
                                    <p>{item.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* Call to Action */}
            <section className="faq-cta">
                <div className="cta-content">
                    <h3>🤔 Vous ne trouvez pas la réponse ?</h3>
                    <p>Notre équipe de support est là pour vous aider !</p>
                    <div className="cta-buttons">
                        <Link to="/contact" className="btn btn-primary">Contactez-nous</Link>
                        <a href={API_PORTAL_URL} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">Voir la documentation</a>
                    </div>
                </div>
            </section>

            <style jsx>{`
                .faq-hero {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    text-align: center;
                    padding: 80px 20px;
                }

                .faq-stats {
                    display: flex;
                    justify-content: center;
                    gap: 3rem;
                    margin-top: 2rem;
                    flex-wrap: wrap;
                }

                .stat {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .stat-number {
                    font-size: 2.5rem;
                    font-weight: bold;
                    color: #ffd700;
                }

                .stat-label {
                    font-size: 1rem;
                    opacity: 0.9;
                    margin-top: 0.5rem;
                }

                .faq-search {
                    background: #f8f9fa;
                    padding: 3rem 20px;
                    text-align: center;
                }

                .search-container {
                    max-width: 600px;
                    margin: 0 auto;
                }

                .search-container h2 {
                    margin-bottom: 1.5rem;
                    color: #495057;
                    font-size: 1.35rem;
                }

                .search-box {
                    display: flex;
                    gap: 1rem;
                    margin-top: 1rem;
                }

                .search-input {
                    flex: 1;
                    padding: 1rem;
                    border: 2px solid #dee2e6;
                    border-radius: 8px;
                    font-size: 1rem;
                    outline: none;
                    transition: border-color 0.3s ease;
                }

                .search-input:focus {
                    border-color: #007bff;
                }

                .search-btn {
                    background: #007bff;
                    color: white;
                    border: none;
                    padding: 1rem 2rem;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.3s ease;
                }

                .search-btn:hover {
                    background: #0056b3;
                }

                .faq-content {
                    padding: 4rem 20px;
                }

                .faq-container {
                    max-width: 800px;
                    margin: 0 auto;
                }

                .faq-item {
                    background: white;
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    margin-bottom: 1rem;
                    overflow: hidden;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    transition: box-shadow 0.3s ease;
                }

                .faq-item:hover {
                    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
                }

                .faq-question {
                    width: 100%;
                    padding: 1.5rem;
                    border: 0;
                    color: inherit;
                    cursor: pointer;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    background: #f8f9fa;
                    border-bottom: 1px solid #dee2e6;
                    font: inherit;
                    text-align: left;
                    transition: background 0.3s ease;
                }

                .faq-question:hover {
                    background: #e9ecef;
                }

                .faq-question h3 {
                    margin: 0;
                    color: #212529;
                    font-size: 1.2rem;
                    font-weight: 600;
                }

                .faq-toggle {
                    font-size: 1.5rem;
                    font-weight: bold;
                    color: #007bff;
                    transition: transform 0.3s ease;
                }

                .faq-toggle.active {
                    transform: rotate(180deg);
                }

                .faq-answer {
                    padding: 1.5rem;
                    background: white;
                    animation: slideDown 0.3s ease;
                }

                .faq-answer p {
                    margin: 0;
                    color: #6c757d;
                    line-height: 1.6;
                    font-size: 1rem;
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .faq-cta {
                    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
                    color: white;
                    text-align: center;
                    padding: 4rem 20px;
                }

                .cta-content h3 {
                    font-size: 2.5rem;
                    margin-bottom: 1rem;
                }

                .cta-content p {
                    font-size: 1.2rem;
                    margin-bottom: 2rem;
                    opacity: 0.9;
                }

                .cta-buttons {
                    display: flex;
                    justify-content: center;
                    gap: 1rem;
                    flex-wrap: wrap;
                }

                .btn {
                    padding: 1rem 2rem;
                    border-radius: 8px;
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 1.1rem;
                    transition: all 0.3s ease;
                    border: 2px solid transparent;
                    display: inline-block;
                }

                .btn-primary {
                    background: #007bff;
                    color: white;
                    border-color: #007bff;
                }

                .btn-primary:hover {
                    background: #0056b3;
                    border-color: #0056b3;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
                    color: white;
                }

                .btn-secondary {
                    background: transparent;
                    color: white;
                    border-color: white;
                }

                .btn-secondary:hover {
                    background: white;
                    color: #28a745;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
                }

                @media (max-width: 768px) {
                    .faq-stats {
                        gap: 2rem;
                    }

                    .search-box {
                        flex-direction: column;
                    }

                    .search-btn {
                        width: 100%;
                    }

                    .faq-question h3 {
                        font-size: 1rem;
                        padding-right: 1rem;
                    }

                    .cta-buttons {
                        flex-direction: column;
                        align-items: center;
                    }

                    .btn {
                        min-width: 200px;
                    }
                }

                .scroll-to-top-btn {
                    position: fixed;
                    bottom: 2rem;
                    right: 2rem;
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border: none;
                    color: white;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
                    transition: all 0.3s ease;
                    z-index: 999;
                }

                .scroll-to-top-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5);
                }

                .scroll-to-top-btn:active {
                    transform: translateY(-1px);
                }

                .scroll-to-top-btn svg {
                    width: 24px;
                    height: 24px;
                }

                @media (max-width: 768px) {
                    .scroll-to-top-btn {
                        bottom: 1.5rem;
                        right: 1.5rem;
                        width: 48px;
                        height: 48px;
                    }

                    .scroll-to-top-btn svg {
                        width: 24px;
                        height: 24px;
                    }
                }

                @media (max-width: 480px) {
                    .scroll-to-top-btn {
                        bottom: 1rem;
                        right: 1rem;
                        width: 45px;
                        height: 45px;
                    }

                    .scroll-to-top-btn svg {
                        width: 20px;
                        height: 20px;
                    }
                }
            `}</style>

            {/* Bouton Scroll to Top */}
            {showScrollButton && (
                <button 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="scroll-to-top-btn"
                    title="Remonter en haut"
                    aria-label="Remonter en haut de la page"
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="18 15 12 9 6 15"></polyline>
                    </svg>
                </button>
            )}
        </>
    );
};

export default FAQ;
